import { isEventKey } from "keycode";
import { matchToolbarOffset } from "../utils";
import { FocusTrap } from "./focus-trap";

const SELECTORS = {
  dialogOpen: ".js-dialog-open",
  dialogClose: ".js-dialog-close",
  dialogHeader: ".js-dialog-header",
};

const CLASSES = {
  hidden: "hidden",
};

class Dialog {
  constructor(context) {
    this.context = context;
    this.dialog = null;
    this.dialogTriggers = null;
    this.dialogDismissers = null;
    this.dialogHeader = null;
    this.focusTrap = null;
    this.onOpenCbStack = [];
    this.onCloseCbStack = [];
  }

  init() {
    this.dialog = this.context.querySelector('[role="dialog"]');
    this.dialogTriggers = [
      ...this.context.querySelectorAll(SELECTORS.dialogOpen),
    ];
    this.dialogDismissers = [
      ...this.context.querySelectorAll(SELECTORS.dialogClose),
    ];
    this.dialogHeader = this.context.querySelector(SELECTORS.dialogHeader);
    this.escHandler = this.escHandler.bind(this);
    if (this.dialog) {
      this.focusTrap = new FocusTrap(this.dialog);
      matchToolbarOffset(this.dialog).apply();
      // Append dialog to end of body tag in order to not depend on container's positioning
      document.body.appendChild(this.dialog);
    }

    this.subscribe();
  }

  subscribe() {
    this.dialogTriggers.forEach((trigger) => {
      trigger.addEventListener("click", (event) => {
        event.preventDefault();
        this.showDialog(trigger);
      });
    });

    this.dialogDismissers.forEach((dismisser) => {
      dismisser.addEventListener("click", (event) => {
        event.preventDefault();
        this.hideDialog();
      });
    });

    this.dialog.addEventListener("keydown", this.tabHandler);
  }

  onOpen(fn) {
    this.onOpenCbStack = [...this.onOpenCbStack, fn];

    return this;
  }

  onClose(fn) {
    this.onCloseCbStack = [...this.onCloseCbStack, fn];

    return this;
  }

  escHandler(event) {
    if (!isEventKey(event, "esc")) {
      return;
    }

    this.hideDialog();
  }

  showDialog(triggeredBy = this.dialogTriggers[0]) {
    const { scrollY } = window;
    const dialogClose = this.dialog.querySelector(SELECTORS.dialogClose);

    // Open dialog
    this.dialog.setAttribute("open", "");
    this.dialog.classList.remove(CLASSES.hidden);

    // Prevent body scroll
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;

    // Init focus trap
    this.focusTrap.init({
      focusFirstElement: dialogClose,
      focusBackElement: triggeredBy,
    });

    // Handle esc close, on document since active element might not be contained in dialog
    document.addEventListener("keyup", this.escHandler);

    this.onOpenCbStack.forEach((fn) => {
      fn(this);
    });

    return this;
  }

  hideDialog() {
    const scrollY = document.body.style.top;

    // Close dialog
    this.dialog.removeAttribute("open");
    this.dialog.classList.add(CLASSES.hidden);

    // Reset body scroll
    document.body.style.position = "";
    document.body.style.top = "";
    window.scrollTo(0, parseInt(scrollY || "0") * -1);
    // Remove esc handler
    document.removeEventListener("keyup", this.escHandler);

    // Suspend focus trap
    this.focusTrap.suspend();

    this.onCloseCbStack.forEach((fn) => {
      fn(this);
    });

    return this;
  }
}

export { Dialog };
